import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, HostListener, ViewChild, ElementRef, SimpleChanges } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ContextItem } from '../../interfaces/chat-component.interfaces';
import { AuthService } from 'src/app/services/auth.service';

export interface ContextItemSelectionEvent {
  item: ContextItem;
  keepOpen: boolean;
}

export interface BusinessSelectedEvent {
  uid: string;
  name: string;
}

export type ContextRootType = 'business' | 'partner' | 'funnel';
export type ContextPopoverSelectionMode = 'attach-context' | 'select-business';

interface ContextRootRecord {
  id: string;
  type: ContextRootType;
  displayName: string;
  searchableText: string;
  imageUrl?: string;
  icon?: string;
  starred?: boolean;
  raw: any;
}

type ContextPopoverView = 'roots' | 'items';
type ContextSegmentId = 'all' | 'tasks' | 'notes' | 'plans' | 'proposals' | 'tickets' | 'apps' | 'files' | 'pages' | 'forms';

interface ContextSegment {
  id: ContextSegmentId;
  label: string;
  count: number;
  types?: ContextItem['type'][];
}

const SOURCE_LABELS: Record<ContextRootType, string> = {
  business: 'Businesses',
  partner: 'Partners',
  funnel: 'Funnels'
};

const SOURCE_ICONS: Record<ContextRootType, string> = {
  business: 'fa-building',
  partner: 'fa-handshake',
  funnel: 'fa-filter'
};

@Component({
  selector: 'app-context-reference-popover',
  templateUrl: './context-reference-popover.component.html',
  styleUrl: './context-reference-popover.component.scss'
})
export class ContextReferencePopoverComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showContextPopover: boolean = false;
  @Input() contextItems: ContextItem[] = [];
  @Input() selectedContextIndex: number = 0;
  @Input() contextUserObject: any = null;
  @Input() agencyObject: any = null;
  @Input() embedded: boolean = false;
  @Input() initialSource: ContextRootType = 'business';
  @Input() preselectedBusinessUid: string = '';
  @Input() availableSources: ContextRootType[] = ['business', 'partner', 'funnel'];
  @Input() selectionMode: ContextPopoverSelectionMode = 'attach-context';
  
  @Output() contextItemSelected = new EventEmitter<ContextItemSelectionEvent>();
  @Output() popoverClosed = new EventEmitter<void>();
  @Output() selectedIndexChanged = new EventEmitter<number>();
  @Output() businessSelected = new EventEmitter<BusinessSelectedEvent>();
  @Output() businessAttached = new EventEmitter<BusinessSelectedEvent>();

  @ViewChild('segmentsContainer') segmentsContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  // View state
  view: ContextPopoverView = 'roots';
  activeSource: ContextRootType = 'business';
  selectedRoot: ContextRootRecord | null = null;

  // Root-level data
  starredBusinesses: any[] = [];
  partnerRoots: ContextRootRecord[] = [];
  funnelRoots: ContextRootRecord[] = [];
  searchResults: ContextRootRecord[] = [];
  searchTerm: string = '';
  rootSelectedIndex: number = 0;
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();
  private starredSub?: Subscription;
  private partnersSub?: Subscription;
  private funnelsSub?: Subscription;
  private searchSub?: Subscription;
  private childItemSubs: Subscription[] = [];

  // Items view
  private _selectedIndex: number = 0;
  childItems: ContextItem[] = [];
  selectedSegment: ContextSegmentId = 'all';
  recentlyAddedItems: Set<string> = new Set();
  segments: ContextSegment[] = [];

  constructor(private afs: AngularFirestore, private auth: AuthService) {}

  ngOnInit(): void {
    this.syncActiveSourceFromInputs();
    this._selectedIndex = this.selectedContextIndex;
    this.updateSegments();
    this.updateSegmentCounts();

    this.searchSub = this.searchSubject.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term || !this.auth.aid || this.activeSource !== 'business') {
          this.isSearching = false;
          return of([]);
        }

        this.isSearching = true;
        return this.afs.collection('users', ref =>
          ref.where('agent', '==', this.auth.aid)
             .where('businessName', '>=', term)
             .where('businessName', '<=', term + '\uf8ff')
             .limit(20)
        ).valueChanges({ idField: 'id' });
      })
    ).subscribe((results: any[]) => {
      this.searchResults = results.map(result => this.normalizeRootRecord(result, 'business'));
      this.isSearching = false;
      this.rootSelectedIndex = 0;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableSources'] && this.availableSources.length > 0 && !this.availableSources.includes(this.activeSource)) {
      this.activeSource = this.availableSources[0];
    }

    if (changes['initialSource'] || changes['availableSources']) {
      this.syncActiveSourceFromInputs();
    }

    this.updateSegments();
    this.updateSegmentCounts();

    if (changes['showContextPopover'] && this.showContextPopover) {
      this.ensureRootDataLoaded(this.activeSource);

      if (!this.selectedRoot || this.view !== 'items') {
        this.resetToRootView();
      }

      if (this.view === 'roots') {
        setTimeout(() => this.focusSearch(), 50);
      }
    }
  }

  ngOnDestroy(): void {
    this.starredSub?.unsubscribe();
    this.partnersSub?.unsubscribe();
    this.funnelsSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.childItemSubs.forEach(sub => sub.unsubscribe());
    this.searchSubject.complete();
  }

  get sourceOptions(): Array<{ id: ContextRootType; label: string; icon: string }> {
    return this.availableSources.map(source => ({
      id: source,
      label: SOURCE_LABELS[source],
      icon: SOURCE_ICONS[source]
    }));
  }

  get activeSourceLabel(): string {
    return SOURCE_LABELS[this.activeSource] || 'Context';
  }

  get searchPlaceholder(): string {
    switch (this.activeSource) {
      case 'partner':
        return 'Search partners...';
      case 'funnel':
        return 'Search funnels...';
      default:
        return 'Search businesses...';
    }
  }

  get emptyStateLabel(): string {
    return this.searchTerm.trim() ? this.activeSourceLabel.toLowerCase() : this.defaultCollectionLabel.toLowerCase();
  }

  get defaultCollectionLabel(): string {
    switch (this.activeSource) {
      case 'partner':
        return 'partners';
      case 'funnel':
        return 'funnels';
      default:
        return 'starred businesses';
    }
  }

  get canBrowseActiveSource(): boolean {
    return this.selectionMode === 'attach-context' && (this.activeSource === 'business' || this.activeSource === 'funnel');
  }

  get canBrowseSelectedRoot(): boolean {
    return !!this.selectedRoot && this.canBrowseSource(this.selectedRoot.type);
  }

  get displayedRoots(): ContextRootRecord[] {
    if (this.activeSource === 'business' && this.searchTerm.trim()) {
      return this.searchResults;
    }

    const roots = this.getRootsForActiveSource();
    const normalizedSearchTerm = this.searchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) {
      return roots;
    }

    return roots.filter(root => root.searchableText.includes(normalizedSearchTerm));
  }

  get filteredItems(): ContextItem[] {
    if (this.selectedSegment === 'all') {
      return this.childItems;
    }

    const segment = this.segments.find(entry => entry.id === this.selectedSegment);
    if (!segment?.types?.length) {
      return this.childItems;
    }

    return this.childItems.filter(item => segment.types?.includes(item.type));
  }

  // ── Root loading ─────────────────────────────────────────

  private ensureRootDataLoaded(source: ContextRootType): void {
    switch (source) {
      case 'partner':
        if (this.partnerRoots.length === 0) {
          this.loadPartners();
        }
        break;
      case 'funnel':
        if (this.funnelRoots.length === 0) {
          this.loadFunnels();
        }
        break;
      default:
        if (this.starredBusinesses.length === 0) {
          this.loadStarredBusinesses();
        }
        break;
    }
  }

  private syncActiveSourceFromInputs(): void {
    const preferredSource = this.availableSources.includes(this.initialSource)
      ? this.initialSource
      : (this.availableSources[0] || 'business');

    this.activeSource = preferredSource;
  }

  private loadStarredBusinesses(): void {
    this.starredSub?.unsubscribe();
    if (!this.auth.aid) return;

    this.starredSub = this.afs.collection('users', ref =>
      ref.where('agent', '==', this.auth.aid)
         .where('cohorts', 'array-contains', 'starred')
         .limit(24)
    ).valueChanges({ idField: 'id' }).subscribe((businesses: any[]) => {
      this.starredBusinesses = businesses;

      if (this.preselectedBusinessUid && !this.selectedRoot) {
        const match = businesses.find(business => this.getRawEntityId(business) === this.preselectedBusinessUid);
        if (match && this.selectionMode === 'attach-context') {
          this.enterRoot(this.normalizeRootRecord(match, 'business'));
        }
      }
    });
  }

  private loadPartners(): void {
    this.partnersSub?.unsubscribe();
    if (!this.auth.aid) return;

    this.partnersSub = this.afs.collection('users', ref =>
      ref.where('associatedAgencyUids', 'array-contains', this.auth.aid)
         .where('archived', '==', false)
         .limit(100)
    ).valueChanges({ idField: 'id' }).subscribe((partners: any[]) => {
      this.partnerRoots = partners
        .map(partner => this.normalizeRootRecord(partner, 'partner'))
        .sort((left, right) => left.displayName.localeCompare(right.displayName));
    });
  }

  private loadFunnels(): void {
    this.funnelsSub?.unsubscribe();
    if (!this.auth.aid) return;

    this.funnelsSub = this.afs.collection(`users/${this.auth.aid}/funnel`, ref =>
      ref.where('archived', '==', false)
         .orderBy('index', 'asc')
         .limit(100)
    ).valueChanges({ idField: 'id' }).subscribe((funnels: any[]) => {
      this.funnelRoots = funnels.map(funnel => this.normalizeRootRecord(funnel, 'funnel'));
    });
  }

  private getRootsForActiveSource(): ContextRootRecord[] {
    switch (this.activeSource) {
      case 'partner':
        return this.partnerRoots;
      case 'funnel':
        return this.funnelRoots;
      default:
        return this.starredBusinesses.map(business => this.normalizeRootRecord(business, 'business'));
    }
  }

  // ── Search ───────────────────────────────────────────────

  onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.rootSelectedIndex = 0;

    if (this.activeSource === 'business' && this.searchTerm.trim()) {
      this.searchSubject.next(this.searchTerm.trim());
    } else {
      this.searchResults = [];
      this.isSearching = false;
    }
  }

  switchSource(source: ContextRootType): void {
    if (this.activeSource === source) {
      return;
    }

    this.activeSource = source;
    this.searchTerm = '';
    this.searchResults = [];
    this.isSearching = false;
    this.rootSelectedIndex = 0;
    this.resetToRootView();
    this.ensureRootDataLoaded(source);
    setTimeout(() => this.focusSearch(), 50);
  }

  onRootClicked(root: ContextRootRecord): void {
    if (this.selectionMode === 'select-business' && root.type === 'business') {
      this.businessSelected.emit({ uid: root.id, name: root.displayName });
      return;
    }

    if (this.canBrowseSource(root.type)) {
      this.enterRoot(root);
      return;
    }

    this.attachRoot(root);
  }

  attachRoot(root: ContextRootRecord, event?: Event): void {
    event?.stopPropagation();

    if (!root.id) {
      return;
    }

    if (this.selectionMode === 'select-business' && root.type === 'business') {
      this.businessAttached.emit({ uid: root.id, name: root.displayName });
      return;
    }

    this.contextItemSelected.emit({
      item: {
        id: root.id,
        type: root.type,
        displayName: root.displayName,
        icon: root.icon
      },
      keepOpen: false
    });
  }

  attachSelectedRoot(event?: Event): void {
    if (!this.selectedRoot) {
      return;
    }

    this.attachRoot(this.selectedRoot, event);
  }

  private enterRoot(root: ContextRootRecord): void {
    this.selectedRoot = root;
    this.view = 'items';
    this.selectedSegment = 'all';
    this._selectedIndex = 0;
    this.selectedIndexChanged.emit(0);
    this.recentlyAddedItems.clear();
    this.updateSegments();
    this.loadChildItems(root);
  }

  goBackToRoots(): void {
    this.resetToRootView();
    setTimeout(() => this.focusSearch(), 50);
  }

  private resetToRootView(): void {
    this.view = 'roots';
    this.searchResults = [];
    this.rootSelectedIndex = 0;
    this.selectedRoot = null;
    this.childItems = [];
    this.selectedSegment = 'all';
    this._selectedIndex = 0;
    this.updateSegments();
    this.updateSegmentCounts();
  }

  private focusSearch(): void {
    this.searchInput?.nativeElement?.focus();
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    const list = this.displayedRoots;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.rootSelectedIndex = Math.min(this.rootSelectedIndex + 1, Math.max(list.length - 1, 0));
        this.scrollRootIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.rootSelectedIndex = Math.max(this.rootSelectedIndex - 1, 0);
        this.scrollRootIntoView();
        break;
      case 'Enter':
        event.preventDefault();
        if (list[this.rootSelectedIndex]) {
          const selectedRoot = list[this.rootSelectedIndex];
          if (event.shiftKey && this.canBrowseSource(selectedRoot.type)) {
            this.attachRoot(selectedRoot);
            return;
          }

          this.onRootClicked(selectedRoot);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.popoverClosed.emit();
        break;
    }
  }

  private scrollRootIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector('.context-popover .context-item.selected');
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }

  // ── Items view ───────────────────────────────────────────

  private loadChildItems(root: ContextRootRecord): void {
    this.childItemSubs.forEach(sub => sub.unsubscribe());
    this.childItemSubs = [];
    this.childItems = [];

    if (!this.auth.aid) {
      this.updateSegmentCounts();
      return;
    }

    const dataByType: Record<string, ContextItem[]> = {};
    const collections = root.type === 'funnel'
      ? [
          { path: `users/${this.auth.aid}/landings`, type: 'page' as const, nameField: 'name', queryField: 'funnelId' },
          { path: `users/${this.auth.aid}/forms`, type: 'form' as const, nameField: 'name', queryField: 'funnelId' }
        ]
      : [
          { path: `users/${root.id}/wireframes`, type: 'plan' as const, nameField: 'name' },
          { path: `users/${root.id}/records`, type: 'note' as const, nameField: 'title' },
          { path: `users/${root.id}/proposals`, type: 'proposal' as const, nameField: 'name' },
          { path: `users/${root.id}/tasks`, type: 'task' as const, nameField: 'name' },
          { path: `users/${root.id}/tickets`, type: 'ticket' as const, nameField: 'subject' },
          { path: `users/${root.id}/apps`, type: 'app' as const, nameField: 'name' },
          { path: `users/${root.id}/media`, type: 'media' as const, nameField: 'name' }
        ];

    collections.forEach(collection => {
      const subscription = this.afs.collection(collection.path, ref => {
        if ('queryField' in collection) {
          return ref.where(collection.queryField, '==', root.id);
        }

        return ref;
      }).valueChanges({ idField: 'id' }).subscribe((items: any[]) => {
        dataByType[collection.type] = items.map(item => this.normalizeChildItem(item, collection.type, collection.nameField, root));
        this.childItems = this.sortChildItems(Object.values(dataByType).flat(), root.type);
        this.updateSegmentCounts();
      });

      this.childItemSubs.push(subscription);
    });
  }

  selectSegment(segment: ContextSegmentId): void {
    this.selectedSegment = segment;
    this._selectedIndex = 0;
    this.selectedIndexChanged.emit(0);
  }

  private updateSegments(): void {
    const rootType = this.selectedRoot?.type;

    if (rootType === 'funnel') {
      this.segments = [
        { id: 'all', label: 'All', count: 0 },
        { id: 'pages', label: 'Pages', count: 0, types: ['page'] },
        { id: 'forms', label: 'Forms', count: 0, types: ['form'] }
      ];
      return;
    }

    this.segments = [
      { id: 'all', label: 'All', count: 0 },
      { id: 'tasks', label: 'Tasks', count: 0, types: ['task'] },
      { id: 'notes', label: 'Notes', count: 0, types: ['note'] },
      { id: 'plans', label: 'Plans', count: 0, types: ['plan'] },
      { id: 'proposals', label: 'Proposals', count: 0, types: ['proposal'] },
      { id: 'tickets', label: 'Tickets', count: 0, types: ['ticket'] },
      { id: 'apps', label: 'Apps', count: 0, types: ['app'] },
      { id: 'files', label: 'Files', count: 0, types: ['media'] }
    ];
  }

  private updateSegmentCounts(): void {
    const counts = this.childItems.reduce<Record<string, number>>((accumulator, item) => {
      accumulator[item.type] = (accumulator[item.type] || 0) + 1;
      return accumulator;
    }, {});

    this.segments = this.segments.map((segment, index) => ({
      ...segment,
      count: index === 0
        ? this.childItems.length
        : (segment.types || []).reduce((total, type) => total + (counts[type] || 0), 0)
    }));
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.showContextPopover) return;

    if (this.view === 'roots') return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      this.cycleSegment(1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      this.cycleSegment(-1);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.popoverClosed.emit();
      return;
    }
    if (event.key === 'Backspace' && this.view === 'items') {
      const active = document.activeElement;
      if (!active || (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA')) {
        event.preventDefault();
        this.goBackToRoots();
        return;
      }
    }
    if (this.filteredItems.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this._selectedIndex = Math.min(this._selectedIndex + 1, this.filteredItems.length - 1);
        this.selectedIndexChanged.emit(this._selectedIndex);
        this.scrollSelectedContextIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this._selectedIndex = Math.max(this._selectedIndex - 1, 0);
        this.selectedIndexChanged.emit(this._selectedIndex);
        this.scrollSelectedContextIntoView();
        break;
      case 'Enter':
        if (this.filteredItems[this._selectedIndex]) {
          event.preventDefault();
          event.stopPropagation();
          const keepOpen = event.shiftKey;
          this.selectContextItem(this.filteredItems[this._selectedIndex], keepOpen);
        }
        break;
    }
  }

  cycleSegment(direction: number): void {
    if (!this.segments || this.segments.length === 0) return;
    const currentIndex = this.segments.findIndex(s => s.id === this.selectedSegment);
    if (currentIndex === -1) {
      this.selectSegment(this.segments[0].id);
      this.scrollSegmentIntoView(0);
      return;
    }
    let nextIndex = currentIndex + direction;
    nextIndex = ((nextIndex % this.segments.length) + this.segments.length) % this.segments.length;
    const nextSegment = this.segments[nextIndex];
    if (nextSegment) {
      this.selectSegment(nextSegment.id);
      this.scrollSegmentIntoView(nextIndex);
    }
  }

  scrollSegmentIntoView(segmentIndex: number): void {
    if (!this.segmentsContainer) return;
    setTimeout(() => {
      const container = this.segmentsContainer?.nativeElement;
      if (!container) return;
      const button = container.querySelector(`[data-segment-index="${segmentIndex}"]`) as HTMLElement;
      button?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 0);
  }

  get currentSelectedIndex(): number {
    return this._selectedIndex;
  }

  selectContextItem(item: ContextItem, keepOpen: boolean = false): void {
    this.contextItemSelected.emit({ item, keepOpen });
    if (keepOpen) {
      const itemKey = `${item.type}-${item.id}`;
      this.recentlyAddedItems.add(itemKey);
    }
  }

  isRecentlyAdded(item: ContextItem): boolean {
    return this.recentlyAddedItems.has(`${item.type}-${item.id}`);
  }

  onStopPropagation(event: Event): void {
    event.stopPropagation();
  }

  getRootLabel(root: ContextRootRecord | null): string {
    return root?.displayName || 'Unnamed';
  }

  getRootInitial(root: ContextRootRecord | null): string {
    const label = this.getRootLabel(root);
    return (label || '?').charAt(0).toUpperCase();
  }

  getRootImageUrl(root: ContextRootRecord | null): string {
    return root?.imageUrl || '';
  }

  isStarred(root: ContextRootRecord | null): boolean {
    return !!root?.starred;
  }

  getContextIconClass(item: ContextItem): string {
    if (item.icon) {
      return item.icon;
    }

    const iconMap: Record<ContextItem['type'], string> = {
      business: 'fa-building',
      partner: 'fa-handshake',
      funnel: 'fa-filter',
      page: 'fa-file-lines',
      form: 'fa-rectangle-list',
      plan: 'fa-sitemap',
      note: 'fa-sticky-note',
      proposal: 'fa-file-signature',
      task: 'fa-square-check',
      ticket: 'fa-headset',
      app: 'fa-rocket',
      media: 'fa-file'
    };

    return iconMap[item.type] || 'fa-paperclip';
  }

  getTypeName(type: string): string {
    const typeNames: { [key: string]: string } = {
      business: 'Business',
      partner: 'Partner',
      funnel: 'Funnel',
      page: 'Page',
      form: 'Form',
      plan: 'Plan',
      note: 'Note',
      proposal: 'Proposal',
      task: 'Task',
      ticket: 'Ticket',
      app: 'App',
      media: 'File'
    };
    return typeNames[type] || type;
  }

  private canBrowseSource(source: ContextRootType): boolean {
    return this.selectionMode === 'attach-context' && (source === 'business' || source === 'funnel');
  }

  private normalizeRootRecord(item: any, type: ContextRootType): ContextRootRecord {
    const id = this.getRawEntityId(item);
    const displayName = this.resolveRootDisplayName(item, type);
    const imageUrl = item?.icon || item?.profilePicture || item?.photoURL || '';
    const searchableText = `${displayName} ${item?.description || ''} ${item?.email || ''}`.toLowerCase();

    return {
      id,
      type,
      displayName,
      searchableText,
      imageUrl,
      icon: SOURCE_ICONS[type],
      starred: type === 'business' ? item?.cohorts?.includes('starred') || false : false,
      raw: item
    };
  }

  private normalizeChildItem(item: any, type: ContextItem['type'], nameField: string, root: ContextRootRecord): ContextItem {
    return {
      ...item,
      id: item?.id || '',
      type,
      displayName: item?.[nameField] || `Untitled ${this.getTypeName(type)}`,
      url: item?.url,
      mediaType: item?.type || item?.mimeType,
      parentId: root.id,
      parentType: root.type,
      parentDisplayName: root.displayName,
      icon: this.getContextIconClass({ id: item?.id || '', type, displayName: '', mediaType: item?.type || item?.mimeType })
    };
  }

  private sortChildItems(items: ContextItem[], rootType: ContextRootType): ContextItem[] {
    const typeOrder: Record<string, number> = rootType === 'funnel'
      ? { page: 1, form: 2 }
      : { plan: 1, note: 2, proposal: 3, task: 4, ticket: 5, app: 6, media: 7 };

    return [...items].sort((left, right) => {
      if (left.type !== right.type) {
        return (typeOrder[left.type] || 99) - (typeOrder[right.type] || 99);
      }

      return left.displayName.localeCompare(right.displayName);
    });
  }

  private resolveRootDisplayName(item: any, type: ContextRootType): string {
    if (type === 'funnel') {
      return item?.name?.trim() || 'Untitled Funnel';
    }

    return item?.businessName?.trim() || item?.brand?.trim() || item?.name?.trim() || (type === 'partner' ? 'Unnamed Partner' : 'Unnamed Business');
  }

  private getRawEntityId(item: any): string {
    return item?.id || item?.uid || '';
  }

  private scrollSelectedContextIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector('.context-item.selected');
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }
}
